/**
 * One chart in the catalog, as a card.
 *
 * The card leads with the picture because that is how people recognise their
 * own work - a title tells you what somebody called it, a preview tells you
 * what it is. The image is the one captured when the chart was saved
 * (`thumbnail.ts`); a chart without one shows its plot-type glyph, the same
 * shape the sidebar's type picker uses, so the fallback still says what kind of
 * chart it is.
 *
 * **The preview loads only when the card is on screen.** An IntersectionObserver
 * per card, exactly as dashboard tiles do it: a catalog of two hundred charts
 * fetches the dozen you can see. Each image is fetched once per chart version
 * and kept as an object URL, so scrolling back costs nothing.
 *
 * **Actions live in a right-click menu, not in a row of buttons.** Five buttons
 * under every card is five times the visual weight of the thing they act on,
 * and a grid of them reads as a control panel rather than a shelf of charts.
 * The two things done often enough to deserve permanence are on the card
 * itself: opening it (click anywhere) and copying its link (one glyph, on
 * hover). Everything else - edit, duplicate, share, rename, delete - is one right
 * click away, which is where people already look for "what else can I do with
 * this".
 *
 * Renaming has no menu-only entry either: the pencil appears on the title when
 * you hover it, because that is the thing being renamed.
 *
 * Delete is press-and-hold rather than click-twice. A second click is easy to
 * do by accident on the same spot; holding for three quarters of a second is
 * not, and the bar filling under the cursor says what will happen while there
 * is still time to let go.
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { fetchChartThumbnail } from '../../api/endpoints.ts';
import type { CatalogChart } from '../../api/types.ts';
import { LightElement } from '../../lit-base.ts';
import { timeAgo } from '../../utils/dates.ts';
import { plotTypeIcon } from '../plot/plot-type-icons.ts';
import { chartSummary, isTemplate, shapeSummary } from './chart-summary.ts';
import { cachedThumbnail, rememberThumbnail } from './thumbnail.ts';

/** How long Delete must be held. Long enough to be deliberate, short enough to not annoy. */
const DELETE_HOLD_MS = 750;

/** Hover delay before a cut-off description shows in full - long enough to not flicker past. */
const DESC_TIP_MS = 350;

/** Globe: in the Public catalog. Stroke on `currentColor`, like the app's other glyphs. */
const GLOBE_GLYPH = svg`<svg class="cat-glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="2" />
  <path
    d="M3.5 12h17M12 3.5c2.6 2.4 3.8 5.2 3.8 8.5s-1.2 6.1-3.8 8.5c-2.6-2.4-3.8-5.2-3.8-8.5S9.4 5.9 12 3.5z"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linejoin="round"
  />
</svg>`;

/** Two people: shared with named colleagues. */
const PEOPLE_GLYPH = svg`<svg class="cat-glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <circle cx="9" cy="9" r="3.5" stroke="currentColor" stroke-width="2" />
  <path
    d="M3 19c.8-3 3.2-4.5 6-4.5s5.2 1.5 6 4.5M15.5 5.8a3.3 3.3 0 0 1 0 6.4M17.5 14.8c1.8.6 3 2 3.5 4.2"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linecap="round"
  />
</svg>`;

@customElement('catalog-card')
export class CatalogCard extends LightElement {
  @property({ attribute: false }) chart!: CatalogChart;
  @property({ attribute: false }) onOpen!: (chart: CatalogChart) => void;
  @property({ attribute: false }) onEdit!: (chart: CatalogChart) => void;
  @property({ attribute: false }) onShare!: (chart: CatalogChart) => void;
  @property({ attribute: false }) onDuplicate!: (chart: CatalogChart) => void;
  @property({ attribute: false }) onDelete!: (chart: CatalogChart) => void;
  @property({ attribute: false }) onRename!: (chart: CatalogChart, title: string) => void;
  @property({ attribute: false }) onCopyLink!: (chart: CatalogChart) => void;
  @property({ attribute: false }) onSetPublic!: (chart: CatalogChart, isPublic: boolean) => void;
  @property({ type: Boolean }) copied = false;

  @state() private preview: string | null = null;
  @state() private renaming = false;
  /**
   * Whether Delete is being held.
   *
   * The bar itself is a CSS transition rather than a per-frame update: the
   * compositor animates it whatever the main thread is doing, which is the
   * difference between a smooth fill and one that stutters while the list
   * re-renders behind it. JavaScript only decides when it starts and stops.
   */
  @state() private holding = false;
  /** Where the right-click menu is open, in viewport coordinates. */
  @state() private menuAt: { x: number; y: number } | null = null;
  /** Where the full description is showing, in viewport coordinates. */
  @state() private descTipAt: { x: number; y: number } | null = null;

  /** The chart thumbnail version `preview` belongs to - see `updated()`. */
  private previewFor: string | null = null;
  private observer: IntersectionObserver | null = null;
  private holdTimer: number | null = null;
  private descTipTimer: number | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    // Nothing is fetched until the card is actually visible.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.observer?.disconnect();
          this.observer = null;
          this.previewFor = this.previewKey();
          void this.loadPreview();
        }
      },
      { rootMargin: '200px' },
    );
    this.observer.observe(this);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.observer?.disconnect();
    this.observer = null;
    this.cancelHold();
    this.closeMenu();
    this.hideDescTip();
  }

  /**
   * Lit reuses elements across renders, so this card can be handed a different
   * chart than the one it drew last time - after a filter, a sort, a delete -
   * or a newly captured thumbnail for the same chart after an edit.
   * `connectedCallback` does not run again for that, so without this the card
   * would keep showing the previous chart's picture under the new chart's name.
   */
  override updated(): void {
    if (this.chart && this.previewFor !== this.previewKey()) {
      this.preview = null;
      this.previewFor = this.previewKey();
      if (!this.observer) void this.loadPreview();
    }
  }

  private previewKey(): string {
    return `${this.chart.id}:${this.chart.thumbnail_version ?? 'none'}`;
  }

  private async loadPreview(): Promise<void> {
    const chartId = this.chart.id;
    const version = this.chart.thumbnail_version;
    if (version == null) return;
    const cached = cachedThumbnail(chartId, version);
    if (cached) {
      this.preview = cached;
      return;
    }
    const url = await fetchChartThumbnail(chartId);
    if (!url) return;
    rememberThumbnail(chartId, version, url);
    // The card may have been handed a different chart while this was in flight.
    if (this.chart.id === chartId && this.chart.thumbnail_version === version) this.preview = url;
  }

  /* ── press-and-hold delete ──────────────────────────────── */

  private startHold(e: PointerEvent): void {
    e.stopPropagation();
    // Capture, so sliding off the button still ends in `pointerup` here rather
    // than leaving a half-filled bar behind.
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    this.holding = true;
    this.holdTimer = window.setTimeout(() => {
      this.cancelHold();
      this.onDelete(this.chart);
    }, DELETE_HOLD_MS);
  }

  /** Let go early and nothing happens - that is the whole safety of it. */
  private cancelHold(): void {
    if (this.holdTimer !== null) window.clearTimeout(this.holdTimer);
    this.holdTimer = null;
    this.holding = false;
  }

  /**
   * Open the menu at the cursor, clamped so it never opens off-screen.
   *
   * The clamp matters on the last row of the grid and the right-hand column,
   * which is exactly where a menu that opens down-and-right would fall out of
   * the viewport - the two places people right-click most, because that is
   * where the newest charts are.
   */
  private openMenu(e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    const width = 200;
    const height = this.chart.can_edit ? 240 : this.chart.can_unpublish ? 160 : 120;
    this.menuAt = {
      x: Math.min(e.clientX, window.innerWidth - width - 8),
      y: Math.min(e.clientY, window.innerHeight - height - 8),
    };
    document.addEventListener('click', this.closeMenu, { once: true });
    document.addEventListener('keydown', this.onMenuKey);
  }

  private closeMenu = (): void => {
    this.menuAt = null;
    this.cancelHold();
    document.removeEventListener('keydown', this.onMenuKey);
  };

  private onMenuKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.closeMenu();
  };

  /* ── the full description ───────────────────────────────── */

  /**
   * Only when the line was actually cut: a tooltip repeating text already on
   * screen in full is noise. Fixed-positioned, like the menu, so the card's
   * `overflow: hidden` and the grid's scroll cannot clip it.
   */
  private showDescTip(e: MouseEvent): void {
    const line = e.currentTarget as HTMLElement;
    if (line.scrollWidth <= line.clientWidth) return;
    this.clearDescTipTimer();
    this.descTipTimer = window.setTimeout(() => {
      const r = line.getBoundingClientRect();
      this.descTipAt = { x: Math.min(r.left - 8, window.innerWidth - 336), y: r.bottom + 6 };
      // The grid scrolls under a fixed tip; drop it rather than leave it behind.
      document.addEventListener('scroll', this.hideDescTip, { capture: true, once: true });
    }, DESC_TIP_MS);
  }

  private hideDescTip = (): void => {
    this.clearDescTipTimer();
    this.descTipAt = null;
    document.removeEventListener('scroll', this.hideDescTip, { capture: true });
  };

  private clearDescTipTimer(): void {
    if (this.descTipTimer !== null) window.clearTimeout(this.descTipTimer);
    this.descTipTimer = null;
  }

  private commitRename(value: string): void {
    const title = value.trim();
    this.renaming = false;
    if (title && title !== this.chart.title) this.onRename(this.chart, title);
  }

  /* ── rendering ──────────────────────────────────────────── */

  /**
   * The Template chip, top-left of the preview.
   *
   * On the picture rather than in the text, because it is the one fact that
   * changes what the picture means: a template's preview is of whatever the
   * saver had open, not of what you will see when you open it.
   */
  private renderTemplateChip(): TemplateResult | typeof nothing {
    if (!isTemplate(this.chart)) return nothing;
    return html`<span
      class="cat-card-template"
      title="Template - opens on whatever ship and time range you have open"
    >
      Template
    </span>`;
  }

  private renderPreview(): TemplateResult {
    const chart = this.chart;
    const stale = chart.thumbnail_version != null && chart.thumbnail_version < chart.version;
    if (this.preview) {
      return html`<div class="cat-card-preview">
        <img src=${this.preview} alt="" loading="lazy" />
        ${this.renderTemplateChip()}
        ${stale
          ? html`<span class="cat-card-stale" title="This chart has been edited since the preview was taken">
              older preview
            </span>`
          : nothing}
      </div>`;
    }
    // No picture: the plot-type shape, large, so the card still says what it is.
    return html`<div class="cat-card-preview is-empty">
      ${this.renderTemplateChip()}
      ${plotTypeIcon(chart.plot_type, 'cat-card-glyph')}
      <span class="cat-card-plot-type">${chart.plot_type}</span>
    </div>`;
  }

  override render(): TemplateResult {
    const chart = this.chart;
    return html`
      <article class="cat-card" @contextmenu=${(e: MouseEvent) => this.openMenu(e)}>
        <button
          class="cat-card-open"
          title="Open in Explore - right-click for more"
          @click=${() => this.onOpen(chart)}
        >
          ${this.renderPreview()}
        </button>
        <!-- The one action worth a permanent control: copying the link is what
             people came to the catalog to do most often after opening. -->
        <button
          class="cat-card-quick ${this.copied ? 'is-ok' : ''}"
          title=${this.copied ? 'Copied' : 'Copy a link to this chart'}
          aria-label="Copy link"
          @click=${(e: Event) => {
            e.stopPropagation();
            this.onCopyLink(chart);
          }}
        >
          ${this.copied ? '✓' : '🔗'}
        </button>

        <div class="cat-card-body">
          <h3 class="cat-card-title">
            ${plotTypeIcon(chart.plot_type, 'cat-card-type')}
            ${this.renaming
              ? html`<input
                  class="cat-card-rename"
                  .value=${chart.title}
                  autofocus
                  @click=${(e: Event) => e.stopPropagation()}
                  @keydown=${(e: KeyboardEvent) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') this.commitRename((e.target as HTMLInputElement).value);
                    if (e.key === 'Escape') this.renaming = false;
                  }}
                  @blur=${(e: Event) => this.commitRename((e.target as HTMLInputElement).value)}
                />`
              : html`<span class="cat-card-name" title=${chart.title}>${chart.title}</span>
                  ${chart.can_edit
                    ? html`<button
                        class="cat-card-edit"
                        title="Rename"
                        aria-label="Rename"
                        @click=${(e: Event) => {
                          e.stopPropagation();
                          this.renaming = true;
                        }}
                      >
                        ✎
                      </button>`
                    : nothing}`}
          </h3>
          <!-- Always rendered, empty or not, so the lines below sit at the same
               height on every card in a row. Tags are no longer shown on the
               card; the search box still finds them. -->
          <p
            class="cat-card-desc"
            @mouseenter=${(e: MouseEvent) => this.showDescTip(e)}
            @mouseleave=${this.hideDescTip}
          >${chart.description ?? ''}</p>
          <!-- A template already says so on its chip; repeating the word here
               would spend the start of a truncated line on it. -->
          <p class="cat-card-meta">${isTemplate(chart) ? shapeSummary(chart) : chartSummary(chart)}</p>
          <p class="cat-card-foot">
            <span title=${new Date(chart.updated_at).toLocaleString()}>
              <span class="cat-card-foot-label">Last modified</span>
              ${timeAgo(chart.updated_at)}
            </span>
            ${chart.is_owner ? this.renderReach() : this.renderAccess()}
          </p>
        </div>
      </article>

      ${this.menuAt ? this.renderMenu() : nothing}
      ${this.descTipAt
        ? html`<div
            class="cat-desc-tip"
            role="tooltip"
            style=${`left:${this.descTipAt.x}px; top:${this.descTipAt.y}px`}
          >
            ${chart.description}
          </div>`
        : nothing}
    `;
  }

  /**
   * Whose chart this is and what you may do with it, bottom right.
   *
   * Only on charts somebody else owns: on "My charts" the answer is always
   * "yours", and a word repeated on every card says nothing. `can_edit` is the
   * server's per-row answer, so the word matches what the menu will offer.
   */
  private renderAccess(): TemplateResult {
    const chart = this.chart;
    const editor = chart.can_edit;
    return html`<span class="cat-card-access">
      <span class="cat-card-owner" title=${chart.owner.email ?? ''}>${chart.owner.display_name}</span>
      <span aria-hidden="true">·</span>
      <span
        class="cat-card-role ${editor ? 'is-editor' : ''}"
        title=${editor ? 'You can edit, rename and share this chart' : 'You can open and duplicate this chart'}
      >
        ${editor ? 'Editor' : 'Viewer'}
      </span>
    </span>`;
  }

  /**
   * How far one of your own charts reaches, in the corner that would otherwise
   * be empty on "My charts": public, shared with some people, or nothing at
   * all when it is private - the quiet default needs no label.
   */
  private renderReach(): TemplateResult | typeof nothing {
    const chart = this.chart;
    if (chart.is_public) {
      return html`<span class="cat-card-reach is-public" title="In the Public catalog - everyone signed in can open it">
        ${GLOBE_GLYPH} Public
      </span>`;
    }
    const count = chart.share_count ?? 0;
    if (!count) return nothing;
    return html`<span
      class="cat-card-reach"
      title=${`Shared with ${count} ${count === 1 ? 'person or group' : 'people and groups'}`}
    >
      ${PEOPLE_GLYPH} Shared · ${count}
    </span>`;
  }

  /** The right-click menu: everything the card no longer wears as buttons. */
  private renderMenu(): TemplateResult {
    const chart = this.chart;
    const at = this.menuAt!;
    return html`<div
      class="cat-menu"
      role="menu"
      style=${`left:${at.x}px; top:${at.y}px`}
      @click=${(e: Event) => e.stopPropagation()}
      @contextmenu=${(e: Event) => e.preventDefault()}
    >
      <button class="cat-menu-item" role="menuitem" @click=${() => {
        this.closeMenu();
        this.onOpen(chart);
      }}>
        <span class="cat-menu-icon" aria-hidden="true">▸</span> Open in Explore
      </button>
      ${chart.can_edit
        ? html`<button class="cat-menu-item" role="menuitem" @click=${() => {
            this.closeMenu();
            this.onEdit(chart);
          }}>
            <span class="cat-menu-icon" aria-hidden="true">✎</span> Edit chart…
          </button>`
        : nothing}
      <button class="cat-menu-item" role="menuitem" @click=${() => {
        this.closeMenu();
        this.onCopyLink(chart);
      }}>
        <span class="cat-menu-icon" aria-hidden="true">🔗</span> Copy link
      </button>
      <button class="cat-menu-item" role="menuitem" @click=${() => {
        this.closeMenu();
        this.onDuplicate(chart);
      }}>
        <span class="cat-menu-icon" aria-hidden="true">❐</span> Duplicate
      </button>
      ${chart.can_edit
        ? html`
            <div class="cat-menu-sep"></div>
            <button class="cat-menu-item" role="menuitem" @click=${() => {
              this.closeMenu();
              this.onShare(chart);
            }}>
              <span class="cat-menu-icon" aria-hidden="true">◈</span> Share…
            </button>
            <button class="cat-menu-item" role="menuitem" @click=${() => {
              this.closeMenu();
              this.onSetPublic(chart, !chart.is_public);
            }}>
              <span class="cat-menu-icon" aria-hidden="true">${GLOBE_GLYPH}</span>
              ${chart.is_public ? 'Make private' : 'Make public'}
            </button>
            <button class="cat-menu-item" role="menuitem" @click=${() => {
              this.menuAt = null;
              this.renaming = true;
            }}>
              <span class="cat-menu-icon" aria-hidden="true">✎</span> Rename
            </button>
            <div class="cat-menu-sep"></div>
            <button
              class="cat-menu-item cat-card-delete is-destructive ${this.holding ? 'is-holding' : ''}"
              role="menuitem"
              title="Press and hold to delete"
              style=${`--hold-ms: ${DELETE_HOLD_MS}ms`}
              @pointerdown=${(e: PointerEvent) => this.startHold(e)}
              @pointerup=${() => this.cancelHold()}
              @pointerleave=${() => this.cancelHold()}
              @pointercancel=${() => this.cancelHold()}
            >
              <span class="cat-card-delete-fill"></span>
              <span class="cat-card-delete-label">
                <span class="cat-menu-icon" aria-hidden="true">×</span> Delete
                <span class="cat-menu-hint">hold</span>
              </span>
            </button>
          `
        : nothing}
      ${!chart.can_edit && chart.can_unpublish && chart.is_public
        ? html`
            <!-- An admin's one power over somebody else's chart: taking it off
                 the public shelf. Not edit, not share, not delete. -->
            <div class="cat-menu-sep"></div>
            <button class="cat-menu-item is-destructive" role="menuitem" @click=${() => {
              this.closeMenu();
              this.onSetPublic(chart, false);
            }}>
              <span class="cat-menu-icon" aria-hidden="true">${GLOBE_GLYPH}</span> Remove from Public
            </button>
          `
        : nothing}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'catalog-card': CatalogCard;
  }
}
